import { AxiosError } from "axios";

import { api } from "@/lib/api";
import {
  oznaciOdbijen,
  ukloni,
  zabiljeziPokusaj,
  zaSlanje,
  type StavkaReda,
} from "@/lib/red";

/**
 * Getting the queue to the server — ADR-0004 §2.3 and §2.4.
 *
 * Sending is a separate step from entry, repeated until it succeeds. It is
 * started by the application: on open, on the `online` event, and on a timer
 * while the app is in the foreground. **Background Sync is not used**, not
 * even where it exists: iOS has no such API, and a mechanism that works on one
 * of the two platforms in use is a mechanism whose failures nobody can
 * reproduce.
 *
 * The server upserts on `client_uuid`, so a batch sent five times produces one
 * row and answers "accepted" every time. That is what makes it safe to retry
 * without knowing whether the previous attempt arrived.
 */

/** How often to try while the app is open. Two minutes is not a magic number:
 * it is short enough that a manager who regains signal in the van sees the
 * queue empty before he parks, and long enough not to matter to the battery. */
export const RAZMAK_MS = 120_000;

/** One entry as the server answered about it. */
interface Ishod {
  client_uuid: string;
  prihvacen: boolean;
  status: string;
  razlog: string | null;
}

interface SyncOdgovor {
  zaprimljeno: number;
  odbijeno: number;
  ishodi: Ishod[];
}

export interface RezultatSlanja {
  /** Entries the server took. Removed from the queue. */
  poslano: number;
  /** Entries it refused. Kept, with the reason, for a person to resolve. */
  odbijeno: number;
  /**
   * Why nothing could be sent, when that is the case. `null` means the attempt
   * reached the server, whatever it then said about individual entries.
   */
  prekid: "nema-veze" | "prijava" | "greska" | null;
}

const NISTA: RezultatSlanja = { poslano: 0, odbijeno: 0, prekid: null };

// One send at a time. The timer, the `online` event and a fresh sign-in can
// all fire within the same second; three overlapping sends would each carry
// the same entries and race each other to clear them.
let uTijeku: Promise<RezultatSlanja> | null = null;

export async function posalji(): Promise<RezultatSlanja> {
  uTijeku ??= izvrsi().finally(() => {
    uTijeku = null;
  });
  return uTijeku;
}

async function izvrsi(): Promise<RezultatSlanja> {
  const stavke = await zaSlanje();
  if (stavke.length === 0) {
    return NISTA;
  }

  // `navigator.onLine` is deliberately not consulted. It reports whether the
  // device has *a* network, not whether this server can be reached, and it is
  // wrong often enough that a queue gated on it can sit full while the phone
  // has perfectly good signal. Unsent hours past the seventh day are a breach
  // of NN 55/24 čl. 15, so the failure that matters is not sending when we
  // could - a wasted request costs a timeout and nothing else.
  try {
    const { data } = await api.post<SyncOdgovor>(
      "/sati/sync",
      stavke.map((stavka: StavkaReda) => stavka.zapis),
    );
    return await primijeni(data.ishodi);
  } catch (error: unknown) {
    return await neuspjeh(error, stavke);
  }
}

/** Clear what was accepted, keep and explain what was not. */
async function primijeni(ishodi: Ishod[]): Promise<RezultatSlanja> {
  let poslano = 0;
  let odbijeno = 0;

  for (const ishod of ishodi) {
    if (ishod.prihvacen) {
      await ukloni(ishod.client_uuid);
      poslano += 1;
    } else {
      // The entry stays on the device with the server's own words attached
      // (§2.6): over twelve hours in a day is refused, and the manager is the
      // only one who knows which of the two entries is wrong.
      await oznaciOdbijen(ishod.client_uuid, ishod.razlog);
      odbijeno += 1;
    }
  }

  return { poslano, odbijeno, prekid: null };
}

/**
 * A failed attempt costs the queue nothing.
 *
 * Nothing is ever dropped here. An expired token while the device was offline
 * is the expected case, not an exception (§2.7): the entries wait, the app
 * asks for a sign-in when there is a connection again, and the next send
 * carries them.
 */
async function neuspjeh(
  error: unknown,
  stavke: StavkaReda[],
): Promise<RezultatSlanja> {
  await zabiljeziPokusaj(stavke.map((stavka) => stavka.zapis.client_uuid));

  if (!(error instanceof AxiosError) || !error.response) {
    return { ...NISTA, prekid: "nema-veze" };
  }
  if (error.response.status === 401 || error.response.status === 403) {
    return { ...NISTA, prekid: "prijava" };
  }
  return { ...NISTA, prekid: "greska" };
}

/**
 * Start the three triggers and return the function that stops them.
 *
 * Deliberately not a React effect on a screen: the queue is emptied whatever
 * the manager is looking at, and a send that only runs on the entry screen is
 * a send that does not happen while he is looking at his crew.
 */
export function pokreniSlanje(
  nakonPokusaja?: (rezultat: RezultatSlanja) => void,
): () => void {
  const pokusaj = () => {
    void posalji().then((rezultat) => nakonPokusaja?.(rezultat));
  };

  pokusaj();
  window.addEventListener("online", pokusaj);
  const tajmer = window.setInterval(pokusaj, RAZMAK_MS);

  return () => {
    window.removeEventListener("online", pokusaj);
    window.clearInterval(tajmer);
  };
}
